import type { CatalogProduct } from "@/lib/catalog";
import { affiliateUrl, featuredProducts, formatBRL } from "@/lib/catalog";
import type { FaqItem } from "@/lib/faqs";
import { homeFaqs } from "@/lib/faqs";
import {
  BUSINESS_EMAIL,
  BUSINESS_LEGAL_NAME,
  BUSINESS_NAME,
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  SITE_URL,
} from "@/lib/site";

const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const PRICE_VALID_UNTIL = "2026-12-31";

function pageUrl(path: string): string {
  if (path === "/" || path === "") return `${SITE_URL}/`;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
}

export function buildOrganizationSchema() {
  return {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: BUSINESS_NAME,
    legalName: BUSINESS_LEGAL_NAME,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/logo.webp`,
    email: BUSINESS_EMAIL,
    sameAs: ["https://www.ybera.com/", "https://www.lojaybera.com.br/"],
  };
}

export function buildWebsiteSchema() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: `${SITE_URL}/`,
    name: BUSINESS_NAME,
    description: DEFAULT_DESCRIPTION,
    inLanguage: "pt-BR",
    publisher: { "@id": ORGANIZATION_ID },
  };
}

function productId(slug: string) {
  return `${SITE_URL}/produto/${slug}/#product`;
}

export function buildProductOfferSchema(product: CatalogProduct) {
  const url = `${SITE_URL}${product.path}`;
  const offerUrl = affiliateUrl(product) ?? url;
  const price = product.price ?? product.regularPrice;
  return {
    "@type": "Product",
    "@id": productId(product.slug),
    name: product.name,
    description: product.shortDescription || product.description.slice(0, 240),
    sku: product.sku,
    image: product.images.length ? product.images : undefined,
    brand: {
      "@type": "Brand",
      name: product.categoryNames.includes("Ybera Paris") ? "Ybera Paris" : "Fashion Gold",
    },
    offers: {
      "@type": "Offer",
      url: offerUrl,
      priceCurrency: "BRL",
      price: price != null ? price.toFixed(2) : undefined,
      priceValidUntil: PRICE_VALID_UNTIL,
      availability: product.inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@id": ORGANIZATION_ID },
    },
  };
}

export function buildFaqSchema(faqs: FaqItem[], pagePath = "/") {
  return {
    "@type": "FAQPage",
    "@id": `${pageUrl(pagePath)}#faq`,
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function buildBreadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: pageUrl(item.path),
    })),
  };
}

export function buildHomeStructuredDataGraph() {
  const featured = featuredProducts();
  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationSchema(),
      buildWebsiteSchema(),
      buildFaqSchema(homeFaqs, "/"),
      {
        "@type": "ItemList",
        "@id": `${SITE_URL}/#featured`,
        name: "Progressiva Fashion Gold em destaque",
        itemListElement: featured.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}${product.path}`,
          name: product.name,
        })),
      },
      ...featured.map((product) => buildProductOfferSchema(product)),
    ],
  };
}

export function buildLandingStructuredDataGraph(
  path: string,
  faqs: FaqItem[],
  products: CatalogProduct[],
) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationSchema(),
      buildWebsiteSchema(),
      buildFaqSchema(faqs, path),
      buildBreadcrumbSchema([
        { name: "Início", path: "/" },
        { name: DEFAULT_TITLE.split("|")[0].trim(), path },
      ]),
      ...products.map((product) => buildProductOfferSchema(product)),
    ],
  };
}

export function buildProductStructuredDataGraph(product: CatalogProduct, faqs: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationSchema(),
      buildProductOfferSchema(product),
      buildFaqSchema(faqs, product.path),
      buildBreadcrumbSchema([
        { name: "Início", path: "/" },
        { name: product.categoryNames[0] || "Produtos", path: "/" },
        { name: product.name, path: product.path },
      ]),
    ],
  };
}

export function formatOfferLabel(product: CatalogProduct): string {
  if (product.regularPrice && product.price && product.regularPrice > product.price) {
    return `${formatBRL(product.price)} (de ${formatBRL(product.regularPrice)})`;
  }
  return formatBRL(product.price);
}
